// TypeScript mirrors of the metrics domain types in
// `packages/roboto/src/roboto/domain/metrics/record.py`. Field names match the
// wire format exactly: `created`/`modified`, `published`/`published_by`,
// `min_timestamp_ns`/`max_timestamp_ns`, snake_case throughout.

import { type Time } from "@/shared/time/time";
import type { Condition } from "@/types";

export const PERIODS = [
  "daily",
  "weekly",
  "monthly",
  "quarterly",
  "yearly",
] as const;
export type Period = (typeof PERIODS)[number];

/** The singular noun for one bucket of a period — "week" for `weekly`. The
 *  period names themselves are adverbs and don't read in running text. */
export const PERIOD_NOUN: Record<Period, string> = {
  daily: "day",
  weekly: "week",
  monthly: "month",
  quarterly: "quarter",
  yearly: "year",
};

export const NUMERIC_AGGREGATIONS = [
  "sum",
  "mean",
  "max",
  "min",
  "count",
] as const;
export type NumericAggregation = (typeof NUMERIC_AGGREGATIONS)[number];

// Wire values from the backend `MetricTimeFilter` StrEnum: the Pydantic
// validator accepts the enum *value* (lowercase), not the Python attribute
// name. Keep these as the literal strings the server expects.
export const METRIC_TIME_FILTERS = ["start_time", "end_time"] as const;
export type MetricTimeFilter = (typeof METRIC_TIME_FILTERS)[number];

/**
 * The ways a chart card can draw its series, in the order the Display picker
 * offers them.
 *
 * `linearAxis` says whether the display can be drawn on a TIME-PROPORTIONAL
 * (linear) x-axis, which is the axis an unaggregated chart uses. Bar cannot:
 * Chart.js sizes bars from the smallest interval between points, and on an
 * irregularly spaced axis that collapses them to hairlines. Line and scatter
 * both position their marks pointwise, so neither cares how the axis is laid
 * out.
 */
export const CHART_DISPLAY_TYPES = [
  { value: "line", label: "Line chart", linearAxis: true },
  { value: "bar", label: "Bar chart", linearAxis: false },
  { value: "scatter", label: "Scatter plot", linearAxis: true },
] as const;
export type ChartDisplayType = (typeof CHART_DISPLAY_TYPES)[number]["value"];

export interface MetricDefinitionRecord {
  org_id: string;
  metric_id: string;
  /** Unique (per-org) name for this metric. */
  name: string;
  description: string | null;
  /**
   * Unit of measure for every value recorded under this metric, e.g. "%",
   * "ms", "m/s". Free-form and unvalidated; `null` means unitless.
   */
  unit: string | null;
  created_by: string;
  created: string; // ISO 8601 with TZ
  modified_by: string;
  modified: string; // ISO 8601 with TZ
}

export interface MetricRecord {
  org_id: string;
  metric_id: string;
  // Fully qualified, dot-separated metric name resolved server-side from the
  // associated `MetricDefinitionRecord`. Mirrors the `name` field added to the
  // Python `MetricRecord` in the SDK.
  name: string;
  session_id: string;
  // The source session's raw int-nanosecond bounds (Unix epoch ns). `null`
  // until the session has at least one file contribution. Mirror of
  // SessionRecord.min_timestamp_ns / max_timestamp_ns.
  min_timestamp_ns: bigint | null;
  max_timestamp_ns: bigint | null;
  value: number;
  // Unit of measure for `value`, resolved server-side from the associated
  // `MetricDefinitionRecord` — same JOIN that resolves `name`, so a value can
  // be labeled without a second lookup. `null` means unitless.
  unit: string | null;
  device_id?: string | null;
  invocation_id?: string | null;
  // The value of the request's `group_by` field for this data point, cast to text server-side so
  // one nullable string covers every groupable field type. Null on an ungrouped query and on a
  // data point carrying no value for the field. Mirrors NumericAggregateMetricRecord.group_key.
  group_key?: string | null;
  published_by: string;
  published: string; // ISO 8601 with TZ
}

export interface NumericAggregateMetricRecord {
  metric_id: string;
  name: string;
  period: Period;
  // Inclusive start / exclusive end of this period bucket, in Unix-epoch
  // nanoseconds (UTC).
  start_time: bigint;
  end_time: bigint;
  total: number;
  value: number;
  // Unit of measure for `value`, resolved from the aggregated metric's
  // definition alongside `name`. `null` means unitless.
  unit: string | null;
  aggregation: NumericAggregation;
  // The value of the request's `group_by` field for the rows behind this
  // bucket, cast to text server-side so one nullable string covers every
  // groupable field type. Absent entirely on an ungrouped response; `null`
  // when the field was unset on those rows, which is a series of its own
  // rather than rows to drop.
  group_key?: string | null;
}

export interface NumericAggregateMetricsResponse {
  aggregation: NumericAggregation;
  records: NumericAggregateMetricRecord[];
}

// -------- Request shapes --------

export interface CreateMetricDefinitionRequest {
  name: string;
  description?: string;
  unit?: string;
}

export interface UpdateMetricDefinitionRequest {
  description?: string;
  /** Omit to leave unchanged (the server's NotSet default); `null` clears it. */
  unit?: string | null;
}

export interface MetricEntry {
  name: string;
  value: number;
}

export interface PublishMetricsRequest {
  session_id: string;
  /** Omit entirely to let the server infer from the session's attached devices.
   *  Pass `null` to record without a device. Pass a string to bind to a device. */
  device_id?: string | null;
  metrics: MetricEntry[];
}

export interface PublishMetricsError {
  name: string;
  error: string;
}

export interface PublishMetricsResponse {
  succeeded: MetricRecord[];
  failed: PublishMetricsError[];
}

export interface QueryMetricsRequest {
  name: string;
  time_filter?: MetricTimeFilter;
  /** Inclusive window start, Unix-epoch ns. `bigint` to preserve full ns
   *  precision end-to-end (the backend field is an arbitrary-precision `int`);
   *  `MetricsService.queryMetrics` serializes it as a bare integer literal via
   *  `rawJSON` rather than a lossy `number`. */
  start_time_ns?: bigint;
  /** Exclusive window end, Unix-epoch ns. See `start_time_ns`. */
  end_time_ns?: bigint;
  max_results?: number;
  /**
   * Return the most recent sessions first (server default: oldest first).
   *
   * A page token is bound to the direction that issued it — the server rejects
   * a continuation that flips it — so this is fixed for the life of a
   * pagination, which is why it belongs in the request object (and therefore
   * in the React Query key) rather than being passed per page.
   */
  descending?: boolean;
  include_device_ids?: string[] | null;
  /** Server-side: only a list is accepted; `null` is rejected at validation time
   *  because `session_id` is non-nullable in the DB. */
  include_session_ids?: string[];
  include_invocation_ids?: string[] | null;
  /** Conditions to filter metrics to query */
  condition?: Condition;
  /**
   * Project this field's value onto each returned data point, under
   * `MetricRecord.group_key`.
   *
   * Unlike the aggregation's `group_by`, this collapses nothing — a raw query already returns one
   * data point per session — so it changes neither which rows come back nor how many. Same field
   * vocabulary as the aggregation's.
   */
  group_by?: string;
}

export interface AggregateMetricsRequest {
  name: string;
  period: Period;
  aggregation: NumericAggregation;
  time_filter?: MetricTimeFilter;
  /** Inclusive window start. Accepts a Date, epoch MILLISECONDS, an ISO 8601
   *  string, or epoch nanoseconds as a bigint —
   *  `MetricsService.aggregateMetric` coerces to ns as it serializes. */
  start_time: Time;
  /** Exclusive window end. See `start_time`. */
  end_time: Time;
  include_device_ids?: string[] | null;
  include_session_ids?: string[];
  include_invocation_ids?: string[] | null;
  /** Conditions to aggregate on filtered metrics */
  condition?: Condition;
  /**
   * Split each time bucket by the distinct values of this field, returning one
   * record per (bucket, value) pair with the value on `group_key`.
   *
   * Named with the same entity-prefixed paths `condition` uses
   * (`device.device_id`, `session.custom.<name>`, `device.custom.<name>`);
   * the server rejects anything outside the groupable set. Omitted, the
   * response is exactly what it was before grouping existed.
   */
  group_by?: string;
}
